import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as Icons from 'lucide-react';
import emailApi from '../services/emailApi';
import EmailBodyFrame from './EmailBodyFrame';

function getLoggedInUser() {
  try {
    const info = JSON.parse(sessionStorage.getItem('user-info') || '{}');
    return info?.user_details || info;
  } catch { return {}; }
}

export default function EmailCompose({ smtpAccounts, activeAccount, mode, initial, onClose, onSent, showToast }) {
  const user = getLoggedInUser();
  const userEmail = user?.email || user?.username || '';
  const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || userEmail;

  // Mail always goes out from the logged-in user's connected mailbox — show that
  // one address only (no picker).
  const fromName = activeAccount?.display_name || userName;
  const fromAddr = activeAccount?.email_address || userEmail;

  const [toAddresses, setToAddresses] = useState([]);
  const [toInput, setToInput] = useState('');
  const [ccAddresses, setCcAddresses] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccAddresses, setBccAddresses] = useState([]);
  const [bccInput, setBccInput] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [candidateResults, setCandidateResults] = useState([]);
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const modalRef = useRef(null);
  const bodyRef = useRef(null);
  const toInputRef = useRef(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    // Reply/Reply All/Forward all start with an EMPTY editable body — the
    // original message is never inserted as quoted text here. It's shown
    // separately, read-only, and properly rendered (see the "Original
    // Message" block below the textarea) — and re-attached at send time.
    if (mode === 'reply' && initial?.message) {
      const msg = initial.message;
      setToAddresses([{ name: msg.from_name || msg.from_address, email: msg.from_address }]);
      setSubject(msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`);
      setBodyHtml('');
    } else if (mode === 'reply_all' && initial?.message) {
      const msg = initial.message;
      const toList = [{ name: msg.from_name || msg.from_address, email: msg.from_address }];
      (msg.to_addresses || []).forEach(a => {
        const addr = typeof a === 'string' ? a : a?.address || '';
        if (addr && addr !== userEmail && addr !== msg.from_address) {
          toList.push({ name: typeof a === 'string' ? a : a?.name || '', email: addr });
        }
      });
      setToAddresses(toList);
      setCcAddresses((msg.cc_addresses || []).map(a => ({
        name: typeof a === 'string' ? a : a?.name || '',
        email: typeof a === 'string' ? a : a?.address || '',
      })));
      setSubject(msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`);
      setBodyHtml('');
    } else if (mode === 'forward' && initial?.message) {
      const msg = initial.message;
      setSubject(msg.subject?.startsWith('Fwd:') ? msg.subject : `Fwd: ${msg.subject}`);
      setBodyHtml('');
    } else if (mode === 'new' && initial?.to) {
      setToAddresses([{ name: initial.toName || initial.to, email: initial.to }]);
    }
  }, [mode, initial]);

  // The message this compose is replying to / forwarding, shown as a
  // separate read-only "Original Message" block (never inside the editable
  // textarea, and never with each line prefixed with "> ").
  const originalMsg = (mode === 'reply' || mode === 'reply_all' || mode === 'forward')
    ? initial?.message
    : null;

  // The compose area is a plain textarea, so quotes must be plain text (not HTML).
  function htmlToText(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    // Drop style/script, and turn block boundaries into line breaks so lists and
    // paragraphs keep their structure once flattened to text.
    tmp.innerHTML = html
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|tr|h[1-6]|ul|ol|table)>/gi, '\n');
    return (tmp.textContent || tmp.innerText || '').replace(/\n{3,}/g, '\n\n').trim();
  }

  // Strip the raw link/image artifacts that Exchange puts in the plain-text part
  // — e.g. "name<mailto:name>", "site<http://site/>", "[cid:...]" and stray <> —
  // so the quoted reply shows only the readable content, not the tags.
  function cleanQuote(text) {
    if (!text) return '';
    return text
      .replace(/\[cid:[^\]]*\]/gi, '')      // inline-image placeholders
      .replace(/<mailto:[^>\n]*>/gi, '')    // mailto link annotations
      .replace(/<https?:\/\/[^>\n]*>/gi, '')// url link annotations
      .replace(/<[^>\n]*>/g, '')            // any remaining <...> artifact / stray <>
      .replace(/[\u200B\uFEFF]/g, '')      // zero-width space / BOM
      .replace(/\u00A0/g, ' ')              // non-breaking space -> space
      .replace(/[ \t]+\n/g, '\n')           // trailing whitespace
      .replace(/\n{3,}/g, '\n\n')           // collapse excess blank lines
      .trim();
  }

  // Some senders' "plain text" part is itself HTML (or otherwise contains
  // markup) rather than genuinely plain text. cleanQuote()'s regex-based tag
  // stripper only ever matches tags on a single line — real Outlook/Exchange
  // HTML routinely wraps long style attributes across multiple lines (e.g.
  // `<div style="...;\n...">`), so those tags survive as literal text and get
  // shown raw in the (plain-textarea) compose box. Detect that case and route
  // through the real DOM-based HTML parser (htmlToText) instead, which safely
  // strips markup regardless of how it's line-wrapped.
  function looksLikeHtml(text) {
    return /<[a-z][a-z0-9]*(\s[^<>]*)?\/?>/i.test(text || '');
  }

  function originalBody(msg) {
    const hasPlainText = msg.body_text && msg.body_text.trim();
    const source = (hasPlainText && !looksLikeHtml(msg.body_text)) ? msg.body_text : htmlToText(msg.body_html || msg.body_text);
    return cleanQuote(source);
  }
  function toEmails(list) {
    return (list || [])
      .map(a => (typeof a === 'string' ? a : a?.address || a?.email || ''))
      .filter(Boolean)
      .join(', ');
  }

  function formatOriginalDate(msg) {
    const raw = msg?.received_at || msg?.sent_at || '';
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  }

  function toSafeHtml(text) {
    return (text || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');
  }

  // The original message's body, ready to be RENDERED (real HTML for HTML
  // mail, safely-escaped line-broken text for plain-text mail) — used for
  // both the on-screen "Original Message" preview and the quote actually
  // attached to the outgoing email. Never routed through the `>`-per-line
  // quoting used for the old inline-textarea behaviour.
  function quoteBodyHtml(msg) {
    if (!msg) return '';
    if (msg.body_html && msg.body_html.trim()) return msg.body_html;
    return toSafeHtml(msg.body_text || '');
  }

  const searchCandidates = useCallback(async query => {
    if (!query || query.length < 2) {
      setCandidateResults([]);
      setShowCandidateDropdown(false);
      return;
    }
    try {
      const res = await emailApi.searchCandidates(query);
      const data = res.data?.results || res.data || [];
      setCandidateResults(Array.isArray(data) ? data : []);
      setShowCandidateDropdown(data.length > 0);
    } catch {
      setCandidateResults([]);
    }
  }, []);

  const handleToInputChange = useCallback(val => {
    setToInput(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchCandidates(val), 300);
  }, [searchCandidates]);

  const addAddress = (field, item) => {
    const addr = typeof item === 'string'
      ? { name: item, email: item }
      : { name: item.candidate_name || item.name || item.email || item.candidate_email, email: item.email || item.candidate_email };
    if (!addr.email) return;
    if (field === 'to') {
      setToAddresses(prev => prev.some(a => a.email === addr.email) ? prev : [...prev, addr]);
      setToInput('');
    } else if (field === 'cc') {
      setCcAddresses(prev => prev.some(a => a.email === addr.email) ? prev : [...prev, addr]);
      setCcInput('');
    } else if (field === 'bcc') {
      setBccAddresses(prev => prev.some(a => a.email === addr.email) ? prev : [...prev, addr]);
      setBccInput('');
    }
    setShowCandidateDropdown(false);
    setCandidateResults([]);
  };

  const removeAddress = (field, email) => {
    if (field === 'to') setToAddresses(prev => prev.filter(a => a.email !== email));
    else if (field === 'cc') setCcAddresses(prev => prev.filter(a => a.email !== email));
    else if (field === 'bcc') setBccAddresses(prev => prev.filter(a => a.email !== email));
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter' && toInput) addAddress(field, toInput);
    if (e.key === 'Backspace' && !toInput && toAddresses.length > 0) {
      setToAddresses(prev => prev.slice(0, -1));
    }
  };

  const handleSend = async () => {
    if (toAddresses.length === 0) {
      showToast('Please add at least one recipient', 'error');
      return;
    }
    if (!activeAccount?.account_id) {
      showToast('No mailbox is connected. Open Mailbox settings first.', 'error');
      return;
    }
    setSending(true);
    try {
      // The compose area is plain text — keep the user's new text as the text
      // body and build a clean HTML version (escape + preserve line breaks)
      // so it renders right.
      const newText = bodyHtml || '';
      let bodyText = newText;
      let bodyHtmlOut = toSafeHtml(newText);

      // Reply/Reply All/Forward: attach the original message as a separate,
      // properly-formatted quote AFTER the user's new text — never merged
      // into it, and never with each line prefixed "> ".
      if (originalMsg) {
        const headerLabel = mode === 'forward' ? 'Forwarded Message' : 'Original Message';
        const from = originalMsg.from_display || originalMsg.from_name || originalMsg.from_address || '';
        const date = formatOriginalDate(originalMsg);
        const toLine = mode === 'forward' ? `To: ${toEmails(originalMsg.to_addresses)}\n` : '';
        const toLineHtml = mode === 'forward' ? `<b>To:</b> ${toEmails(originalMsg.to_addresses)}<br>` : '';

        bodyText = `${newText}\n\n`
          + `---------------- ${headerLabel} ----------------\n`
          + `From: ${from}\n`
          + `Date: ${date}\n`
          + toLine
          + `Subject: ${originalMsg.subject || ''}\n\n`
          + originalBody(originalMsg);

        bodyHtmlOut = `${toSafeHtml(newText)}<br><br>`
          + `<div style="border-top:1px solid #e1e1e1;margin-top:8px;padding-top:12px;color:#666;font-size:13px;">`
          + `<p style="margin:0 0 8px;"><b>---------------- ${headerLabel} ----------------</b></p>`
          + `<p style="margin:0 0 12px;"><b>From:</b> ${from}<br><b>Date:</b> ${date}<br>${toLineHtml}<b>Subject:</b> ${originalMsg.subject || ''}</p>`
          + `</div>`
          + `<div>${quoteBodyHtml(originalMsg)}</div>`;
      }

      const subjectVal = subject.trim() || '(no subject)';

      // General outgoing-mail API: send to anyone with Cc/Bcc + real file
      // attachments (multipart/form-data), independent of any job order.
      const fd = new FormData();
      fd.append('account_id', activeAccount.account_id);
      toAddresses.forEach(a => fd.append('to_addresses', a.email));
      (showCc ? ccAddresses : []).forEach(a => fd.append('cc_addresses', a.email));
      (showBcc ? bccAddresses : []).forEach(a => fd.append('bcc_addresses', a.email));
      fd.append('subject', subjectVal);
      fd.append('body_html', bodyHtmlOut);
      fd.append('body_text', bodyText);
      // Thread replies back to the original message.
      if ((mode === 'reply' || mode === 'reply_all') && initial?.message?.message_id) {
        fd.append('in_reply_to', initial.message.message_id);
      }
      attachments.forEach(a => {
        if (a.file) fd.append('attachments', a.file, a.name);
      });

      await emailApi.sendMailboxEmail(fd);
      showToast('Email sent successfully', 'success');
      // The mail is already sent — don't let a post-send refresh error turn this
      // into a false "failed to send".
      try { onSent && onSent(); } catch (e) { /* ignore */ }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to send email';
      showToast(msg, 'error');
    } finally {
      setSending(false);
    }
  };

  const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5 MB per email

  const handleFileSelect = e => {
    const files = Array.from(e.target.files);

    // Reject any single file larger than 5 MB.
    const oversized = files.filter(f => f.size > MAX_ATTACHMENT_BYTES);
    const accepted = files.filter(f => f.size <= MAX_ATTACHMENT_BYTES);

    if (oversized.length) {
      const names = oversized.map(f => f.name).join(', ');
      showToast?.(`Attachment too large (max 5 MB per email): ${names}`, 'error');
    }

    setAttachments(prev => {
      // Also cap the combined size of all attachments at 5 MB per email.
      let total = prev.reduce((sum, a) => sum + (a.size || 0), 0);
      const next = [...prev];
      for (const f of accepted) {
        if (total + f.size > MAX_ATTACHMENT_BYTES) {
          showToast?.(`Total attachments exceed the 5 MB per-email limit. "${f.name}" was not added.`, 'error');
          continue;
        }
        total += f.size;
        next.push({ id: null, name: f.name, size: f.size, file: f });
      }
      return next;
    });

    // Allow re-selecting the same file after a rejection.
    e.target.value = '';
  };

  const removeAttachment = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = useCallback(e => {
    const rect = modalRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ox = e.clientX - rect.left;
    const oy = e.clientY - rect.top;
    const mm = me => {
      if (modalRef.current) {
        // Drop the centering transform so top/left are absolute while dragging.
        modalRef.current.style.transform = 'none';
        modalRef.current.style.left = `${me.clientX - ox}px`;
        modalRef.current.style.top = `${me.clientY - oy}px`;
        modalRef.current.style.bottom = 'auto';
        modalRef.current.style.right = 'auto';
      }
    };
    const mu = () => { document.removeEventListener('mousemove', mm); document.removeEventListener('mouseup', mu); };
    document.addEventListener('mousemove', mm);
    document.addEventListener('mouseup', mu);
  }, []);

  const renderTags = (addresses, field) => (
    <div className="em-compose-tag-container">
      {addresses.map(a => (
        <span key={a.email} className="em-compose-tag">
          {a.name || a.email}
          <span className="em-compose-tag-remove" onClick={() => removeAddress(field, a.email)}>
            <Icons.X size={10} />
          </span>
        </span>
      ))}
      <input
        className="em-compose-tag-input"
        placeholder={addresses.length === 0 ? 'Type name or email' : ''}
        value={field === 'to' ? toInput : field === 'cc' ? ccInput : bccInput}
        onChange={e => {
          const val = e.target.value;
          if (field === 'to') handleToInputChange(val);
          else if (field === 'cc') setCcInput(val);
          else setBccInput(val);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            const v = field === 'to' ? toInput : field === 'cc' ? ccInput : bccInput;
            if (v) addAddress(field, v);
          }
          if (field === 'to' && e.key === 'Backspace' && !toInput && toAddresses.length > 0) {
            setToAddresses(prev => prev.slice(0, -1));
          }
        }}
        ref={field === 'to' ? toInputRef : undefined}
      />
    </div>
  );

  return (
    <div
      ref={modalRef}
      className={`em-compose-overlay ${mode === 'new' ? 'em-compose-fullscreen' : ''}`}
    >
      <div className="em-compose-header" onMouseDown={handleDrag}>
        <h3>
          {mode === 'reply' ? 'Reply' : mode === 'reply_all' ? 'Reply All' : mode === 'forward' ? 'Forward' : 'New Message'}
        </h3>
        <div className="em-compose-header-actions">
          <button className="em-compose-header-btn" onClick={onClose} title="Close">
            <Icons.X size={14} />
          </button>
        </div>
      </div>

      <div className="em-compose-body">
        <div className="em-compose-field">
          <span className="em-compose-field-label">From</span>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0' }}>
            <span style={{ fontSize: 12, color: '#1a1a1a' }}>
              {fromName ? `${fromName} <${fromAddr}>` : fromAddr}
            </span>
          </div>
        </div>

        <div className="em-compose-field em-compose-to-field">
          <span className="em-compose-field-label">To</span>
          {renderTags(toAddresses, 'to')}
          <span style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            <button
              className="em-compose-toggle-cc"
              onClick={() => setShowCc(!showCc)}
              style={showCc ? { fontWeight: 600 } : {}}
            >
              Cc
            </button>
            <button
              className="em-compose-toggle-cc"
              onClick={() => setShowBcc(!showBcc)}
              style={showBcc ? { fontWeight: 600 } : {}}
            >
              Bcc
            </button>
          </span>
          {showCandidateDropdown && candidateResults.length > 0 && (
            <div className="em-compose-candidate-list">
              {candidateResults.map((c, i) => (
                <div key={c.id || i} className="em-compose-candidate-item" onClick={() => addAddress('to', c)}>
                  <span className="em-compose-candidate-avatar">
                    {(c.candidate_name || c.name || '?')[0]?.toUpperCase()}
                  </span>
                  <div className="em-compose-candidate-info">
                    <div className="em-compose-candidate-name">{c.candidate_name || c.name || c.email || c.candidate_email}</div>
                    <div className="em-compose-candidate-email">{c.email || c.candidate_email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showCc && (
          <div className="em-compose-field">
            <span className="em-compose-field-label">Cc</span>
            {renderTags(ccAddresses, 'cc')}
          </div>
        )}

        {showBcc && (
          <div className="em-compose-field">
            <span className="em-compose-field-label">Bcc</span>
            {renderTags(bccAddresses, 'bcc')}
          </div>
        )}

        <div className="em-compose-field">
          <span className="em-compose-field-label" style={{ borderBottom: 'none' }}>Subject</span>
          <div className="em-compose-subject-field" style={{ flex: 1 }}>
            <input placeholder="Add a subject" value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
        </div>

        <div className="em-compose-toolbar">
          <button className="em-compose-tool-btn" title="Bold"><Icons.Bold size={13} /></button>
          <button className="em-compose-tool-btn" title="Italic"><Icons.Italic size={13} /></button>
          <button className="em-compose-tool-btn" title="Underline"><Icons.Underline size={13} /></button>
          <button className="em-compose-tool-btn" title="Attach File" onClick={() => document.getElementById('em-compose-file-input').click()}>
            <Icons.Paperclip size={13} />
          </button>
          <input id="em-compose-file-input" type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
        </div>

        <div className="em-compose-editor">
          <textarea
            ref={bodyRef}
            placeholder="Type your message here"
            value={bodyHtml}
            onChange={e => setBodyHtml(e.target.value)}
          />
        </div>

        {originalMsg && (
          <div className="em-compose-quote">
            <div className="em-compose-quote-sep">
              ---------------- {mode === 'forward' ? 'Forwarded Message' : 'Original Message'} ----------------
            </div>
            <div className="em-compose-quote-meta">
              <div><b>From:</b> {originalMsg.from_display || originalMsg.from_name || originalMsg.from_address || ''}</div>
              <div><b>Date:</b> {formatOriginalDate(originalMsg)}</div>
              {mode === 'forward' && <div><b>To:</b> {toEmails(originalMsg.to_addresses)}</div>}
              <div><b>Subject:</b> {originalMsg.subject || ''}</div>
            </div>
            <div className="em-compose-quote-body">
              <EmailBodyFrame html={quoteBodyHtml(originalMsg)} />
            </div>
          </div>
        )}

        {attachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 8px' }}>
            {attachments.map((att, i) => (
              <span key={i} className="em-compose-tag">
                <Icons.Paperclip size={10} />
                {att.name}
                <span className="em-compose-tag-remove" onClick={() => removeAttachment(i)}>
                  <Icons.X size={10} />
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="em-compose-footer">
        <div className="em-compose-footer-left">
          <button className="em-compose-send-btn" onClick={handleSend} disabled={sending}>
            {sending ? (
              <><span className="em-spinner" style={{ width: 12, height: 12 }} /> Sending...</>
            ) : (
              <><Icons.Send size={14} /> Send</>
            )}
          </button>
          <button className="em-compose-attach-btn" title="Attach File" onClick={() => document.getElementById('em-compose-file-input').click()}>
            <Icons.Paperclip size={14} />
          </button>
        </div>
        <div className="em-compose-footer-right">
          <button className="em-compose-trash-btn" title="Discard" onClick={onClose}>
            <Icons.Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
